import type { TableColumn } from "@/types";

export type TableRowEditorInputType =
  | "checkbox"
  | "date"
  | "datetime-local"
  | "number"
  | "select"
  | "text"
  | "textarea";

export interface TableRowEditorField {
  key: string;
  label: string;
  inputType: TableRowEditorInputType;
  required: boolean;
  options: string[];
}

const metadataFields = new Set([
  "id",
  "_id",
  "recordId",
  "record_id",
  "documentId",
  "document_id",
  "__v",
  "subRows",
  "schemaId",
  "schemaVersion",
  "groupId",
  "organizationId",
  "metadata",
  "createdAt",
  "updatedAt",
]);

const actionFieldTokens = ["action", "actions"];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getStringMetaValue = (
  meta: Record<string, unknown> | undefined,
  keys: string[],
): string | undefined => {
  if (!meta) return undefined;

  for (const key of keys) {
    const value = meta[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }

  return undefined;
};

const getStringOptions = (
  meta: Record<string, unknown> | undefined,
): string[] => {
  const rawOptions = meta?.options ?? meta?.values;
  if (!Array.isArray(rawOptions)) return [];

  return rawOptions.flatMap((option) => {
    if (typeof option === "string" || typeof option === "number") {
      return [String(option)];
    }
    if (!isRecord(option)) return [];

    const value = option.value ?? option.label;
    return typeof value === "string" || typeof value === "number"
      ? [String(value)]
      : [];
  });
};

const uniqueStrings = (values: string[]): string[] => {
  const seen = new Set<string>();
  const unique: string[] = [];
  values.forEach((value) => {
    if (seen.has(value)) return;
    seen.add(value);
    unique.push(value);
  });
  return unique;
};

const formatHeader = (key: string): string =>
  key
    .replace(/^(avg|sum|count|min|max|none)_/, "")
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .trim();

const shouldSkipField = (key: string): boolean => {
  if (metadataFields.has(key)) return true;
  const normalized = key.toLowerCase();
  return actionFieldTokens.some((token) => normalized === token);
};

const inferInputType = (
  key: string,
  value: unknown,
  meta: Record<string, unknown> | undefined,
): TableRowEditorInputType => {
  const declaredType = getStringMetaValue(meta, [
    "inputType",
    "fieldType",
    "type",
    "dataType",
  ])?.toLowerCase();

  if (declaredType === "boolean" || declaredType === "checkbox") return "checkbox";
  if (declaredType === "number" || declaredType === "integer") return "number";
  if (declaredType === "date") return "date";
  if (declaredType === "datetime" || declaredType === "datetime-local") return "datetime-local";
  if (declaredType === "textarea" || declaredType === "multiline") return "textarea";
  if (declaredType === "select" || declaredType === "enum") return "select";

  if (typeof value === "boolean") return "checkbox";
  if (typeof value === "number") return "number";

  const normalizedKey = key.toLowerCase();
  if (normalizedKey.includes("date")) return "date";
  if (
    normalizedKey.includes("count") ||
    normalizedKey.includes("amount") ||
    normalizedKey.includes("price") ||
    normalizedKey.includes("salary") ||
    normalizedKey.includes("total") ||
    normalizedKey.includes("value")
  ) {
    return "number";
  }

  return "text";
};

const getFieldOptionsFromRows = (
  key: string,
  rows: Record<string, unknown>[],
): string[] => {
  const values = rows.flatMap((row) => {
    const value = row[key];
    return typeof value === "string" && value.trim().length > 0
      ? [value]
      : [];
  });
  return uniqueStrings(values).sort((left, right) => left.localeCompare(right));
};

export const createTableRowEditorFields = (
  columns: TableColumn[],
  rows: Record<string, unknown>[],
  rowData: Record<string, unknown> = {},
): TableRowEditorField[] => {
  const columnKeys = new Set(columns.map((column) => column.accessorKey));
  const columnFields = columns
    .filter((column) => !shouldSkipField(column.accessorKey))
    .map((column) => {
      const meta = isRecord(column.meta) ? column.meta : undefined;
      const options = uniqueStrings([
        ...getStringOptions(meta),
        ...getFieldOptionsFromRows(column.accessorKey, rows),
      ]);
      const required = meta?.required === true;

      return {
        key: column.accessorKey,
        label: column.header || formatHeader(column.accessorKey),
        inputType: inferInputType(
          column.accessorKey,
          rowData[column.accessorKey],
          meta,
        ),
        required,
        options,
      };
    });

  const extraRowFields = Object.keys(rowData)
    .filter((key) => !columnKeys.has(key))
    .filter((key) => !shouldSkipField(key))
    .map((key) => ({
      key,
      label: formatHeader(key),
      inputType: inferInputType(key, rowData[key], undefined),
      required: false,
      options: [],
    }));

  return [...columnFields, ...extraRowFields];
};
